"use client";

import { addToast, Button } from "@heroui/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { services } from "../../../../public/services";
import { useAuthStore } from "../../../../public/store/auth";
import { useRouter } from "next/navigation";
import { getFirstAccessiblePath } from "../../Constants/menu";

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuthStore();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember: false,
    },
  });

  const loginMutation = useMutation({
    mutationFn: (payload) => services.auth.login.call(payload, {
      skipAuth: true, // 🔥 THIS IS IMPORTANT
    }),
    onSuccess: async (res) => {
      setIsLoading(false);
      // store response in Zustand
      login(res);
      addToast({
        title: "Success",
        description: "Login successfully!",
        color: "success",
        timeout: 2000,
      });

      try {
        const permissionsResp =
          await services.permissions.getMyPermissions.call();
        const userType = res?.data?.data?.user?.user_type;
        const firstPath = getFirstAccessiblePath(
          userType,
          permissionsResp.data
        );
        router.push(firstPath);
      } catch (error) {
        console.error("Error fetching permissions for redirect:", error);
        router.push("/dashboard");
      }
    },
    onError: (error) => {
      console.log("error", error);
      setIsLoading(false);

      const resData = error.response?.data;
      let errorMessage = "Failed to Login";

      if (resData) {
        if (resData.email) {
          errorMessage = Array.isArray(resData.email) ? resData.email[0] : resData.email;
        } else if (resData.password) {
          errorMessage = Array.isArray(resData.password) ? resData.password[0] : resData.password;
        } else if (resData.non_field_errors) {
          errorMessage = Array.isArray(resData.non_field_errors) ? resData.non_field_errors[0] : resData.non_field_errors;
        } else if (resData.detail) {
          errorMessage = resData.detail;
        } else if (resData.message) {
          errorMessage = resData.message;
        } else if (typeof resData === "string") {
          errorMessage = resData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      addToast({
        title: "Error",
        description: errorMessage,
        color: "danger",
        timeout: 2000,
      });
    },
  });

  const onSubmit = async (values) => {
    setIsLoading(true);
    const payload = {
      email: values.email,
      password: values.password,
    };
    loginMutation.mutate(payload);
  };

  return (
    <div>
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-black">Login</h1>
        <p className="mt-2 text-sm text-[var(--primary)]">
          Enter your email and password to continue accessing your account.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-foreground"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            placeholder="Enter email"
            className="block w-full rounded-lg border border-[var(--border)] bg-[var(--color-card)] px-3 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-[var(--color-ring)]"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Enter a valid email address",
              },
            })}
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-2 text-sm text-red-500">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="mb-2 block text-sm font-medium text-foreground"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              className="block w-full rounded-lg border border-[var(--border)] bg-[var(--color-card)] px-3 py-3 pr-11 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-[var(--color-ring)]"
              {...register("password", {
                required: "Password is required",
                minLength: { value: 6, message: "Minimum 6 characters" },
              })}
              aria-invalid={errors.password ? "true" : "false"}
              aria-describedby={errors.password ? "password-error" : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                // eye-off
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l3.59 3.59A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411L21 21"
                  />
                </svg>
              ) : (
                // eye
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" className="mt-2 text-sm text-red-500">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember / Forgot */}
        <div className="flex items-center justify-between">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground font-medium">
            <input
              type="checkbox"
              className="size-4 cursor-pointer rounded border-[var(--color-input)] text-[var(--color-brand)] "
              {...register("remember")}
            />
            Remember me
          </label>
          <a
            href="/forgotpassword/"
            className="text-sm font-medium text-[var(--primary)] hover:underline"
          >
            Forgot Password?
          </a>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full btn-primary px-4 py-3 text-center text-base font-semibold text-[var(--color-brand-foreground)] hover:opacity-95 disabled:opacity-70"
        >
          {isLoading ? "Logging in..." : "Login"}
        </Button>
      </form>
    </div>
  );
}
